import { PROGRESS } from 'config/config';
import { getContainerHeight, loadScript } from 'helpers/commonHelper';
import withLocaleContext from 'hoc/withLocaleContext';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { Container, ErrorContainer } from './CardFormWL.styles';
import withSaferPayScriptContext from '../../../../hoc/WithSaferPayScriptContext';
import { getWorldlineConfiguration } from '../../../../services/Api';
import { getPlaceHolders, widgetStyles } from './CardFormWL.constant';
import CardWidgetFieldsValidator from './CardWidgetFieldsValidator';
import ChevronButton from '../savedInstruments/chevronButton/ChevronButton';
import ChevronLeft from '../../../../assets/images/chevronLeft.png';
import withCardContext from '../../../../hoc/withCardContext';
import {
  CardFieldEvent,
  CardWidgetFieldsValidatorResult,
  ExternalScriptState,
  ICardEvent,
  SavedInstrumentsProps,
} from './CardFormWL.types';
import { ICardProps, ICardWidget } from '../../Card.types';
import { ISaferPayScriptContext } from '../../../../types/SaferPayScriptContext';
import { ICardContext } from '../../../../types/CardContext';
import { getOldEvents, getValidEvents, VALIDATION_TIMEOUT } from './CardFormWLUtils';
import CardFormTemplate from './cardFormTemplate/CardFormTemplate';
import withCardFormMetrics from '../../../../hoc/withCardFormMetrics';
import { ICardFormMetrics } from '../../../../services/CardFormMetricsContext';

const CARD_PREFIX = 'paymentMethod.description.CARD';

const PLACEHOLDER_PREFIX = `${CARD_PREFIX}.field.placeholder`;

const isCardFormStuck = (validateFields: Map<string, CardFieldEvent>) =>
  validateFields.size === 3 && getOldEvents(validateFields).length > 0;

const shouldMonitorUserInactivity = (validateFields: Map<string, CardFieldEvent>) => validateFields.size === 3;

const getInitialState = (
  isSaferPayFieldsScriptLoad: boolean,
  isCardComponentLoading: boolean,
  isSaferPaySuccessCalled: boolean
) => {
  if (isSaferPayFieldsScriptLoad) {
    if (!isCardComponentLoading && isSaferPaySuccessCalled) {
      return ExternalScriptState.INITIALIZED;
    }
    return ExternalScriptState.READY;
  }
  return ExternalScriptState.NOT_INIT;
};

function isCardFormFilled(validateFields: Map<string, CardFieldEvent>) {
  return getValidEvents(validateFields).length === 4;
}

function CardFormWL(
  props: ICardProps &
    ICardWidget &
    ISaferPayScriptContext &
    SavedInstrumentsProps &
    ICardContext &
    Partial<ICardFormMetrics>
) {
  const {
    widgetUrl,
    isSaferPayFieldsScriptLoad,
    setSaferPayScriptLoadAndConfig,
    match: {
      params: { paymentId },
    },
    saferPayFields,
    setSaferPayFields,
    setSaferPayToken,
    saferPayToken,
    worldlineConfiguration,
    setTokenizationEnabled,
    paymentMethods,
    setIsCardComponentLoading,
    setProgress,
    progress,
    setHeight,
    hasSavedInstruments,
    handleShowSavedInstrumentClick,
    formatMessage,
    isCardComponentLoading,
    trackEvent,
    resetTrackEvent,
  } = props;

  const containerRef = useRef(null);
  const cardWidgetFieldsValidator = useRef(new CardWidgetFieldsValidator(trackEvent)).current;
  const [validateFields, setValidateFields] = useState<Map<string, CardFieldEvent>>(new Map<string, CardFieldEvent>());
  const [isSaferPaySuccessCalled, setIsSaferPaySuccessCalled] = useState<boolean>(false);
  const [externalScriptState, setExternalScriptState] = useState<ExternalScriptState>(
    getInitialState(isSaferPayFieldsScriptLoad, isCardComponentLoading, isSaferPaySuccessCalled)
  );

  const callValidatorAndSetProgress = (cardEvent: ICardEvent) => {
    if (cardWidgetFieldsValidator.isBlockedByCardNumberValidation(cardEvent) || saferPayToken !== undefined) return;

    const result = cardWidgetFieldsValidator.getValidationResult(cardEvent);
    switch (result) {
      case CardWidgetFieldsValidatorResult.SHOW_LOADER:
        setProgress(PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_PENDING);
        break;
      case CardWidgetFieldsValidatorResult.SHOW_PAYMENT_BUTTON:
        setProgress(PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_SUCCESS);
        break;
      default:
        setProgress(PROGRESS.CARD_WORLDLINE_PAYMENT_FIELDS_VALIDATION);
        break;
    }
  };

  const addValidateFieldsMap = (cardEvent: ICardEvent) => {
    const event = new CardFieldEvent(cardEvent);
    setValidateFields(prevMap => new Map(prevMap).set(event.id, event));
  };

  useEffect(() => {
    if (saferPayToken && !isSaferPaySuccessCalled && validateFields.size === 0) {
      setSaferPayToken(undefined);
    }
  }, [saferPayToken, isSaferPaySuccessCalled, validateFields]);

  useEffect(() => {
    if (isCardFormFilled(validateFields) && progress < PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_SUCCESS) {
      setProgress(PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_SUCCESS);
    }
  }, [progress, validateFields]);

  useEffect(() => {
    if (shouldMonitorUserInactivity(validateFields)) {
      const interval = setInterval(() => {
        if (isCardFormStuck(validateFields)) {
          // @ts-ignore
          saferPayFields.submit({
            onSuccess(evt) {
              /* eslint-disable */
              setSaferPayToken(evt.token);
            },
          });
        }
      }, VALIDATION_TIMEOUT);

      return () => {
        clearInterval(interval);
      };
    }
    return undefined;
  }, [validateFields, progress]);

  useEffect(() => {
    cardWidgetFieldsValidator.init();
    resetTrackEvent?.();
    let error = false;
    if (!isSaferPayFieldsScriptLoad) {
      const load = async () => {
        await loadScript(widgetUrl)
          .then(async () => {
            const response = await getWorldlineConfiguration(paymentId);
            setSaferPayScriptLoadAndConfig(true, response);
          })
          .catch(() => {
            error = true;
          })
          .finally(() => {
            setExternalScriptState(error ? ExternalScriptState.ERROR : ExternalScriptState.READY);
          });
      };

      load().then();
    }
  }, [isSaferPayFieldsScriptLoad, paymentId, widgetUrl]);

  useEffect(() => {
    setHeight(getContainerHeight(containerRef.current));
    setSaferPayFields(window.SaferpayFields);
  }, [externalScriptState, isSaferPayFieldsScriptLoad, paymentMethods, saferPayFields, worldlineConfiguration]);

  useEffect(() => {
    if (
      externalScriptState === ExternalScriptState.READY &&
      isSaferPayFieldsScriptLoad &&
      worldlineConfiguration &&
      saferPayFields
    ) {
      setTokenizationEnabled(paymentMethods[0]?.tokenizationEnabled);
      const holders = getPlaceHolders(formatMessage, PLACEHOLDER_PREFIX);

      // @ts-ignore
      saferPayFields.init({
        ...worldlineConfiguration,
        style: widgetStyles,
        placeholders: holders,
        onError() {
          setExternalScriptState(ExternalScriptState.ERROR);
          setIsCardComponentLoading(false);
        },
        onSuccess() {
          setIsCardComponentLoading(false);
          setIsSaferPaySuccessCalled(true);
        },
        onValidated(cardEvent: ICardEvent) {
          addValidateFieldsMap(cardEvent);
          callValidatorAndSetProgress(cardEvent);
        },
        onBlur(cardEvent: ICardEvent) {
          callValidatorAndSetProgress(cardEvent);
        },
      });

      setExternalScriptState(ExternalScriptState.INITIALIZED);
    }
  }, [isSaferPayFieldsScriptLoad, externalScriptState, worldlineConfiguration]);

  return (
    <Container
      className="card-component__wl-container"
      hasExtendedHeight={externalScriptState === ExternalScriptState.ERROR || hasSavedInstruments}
      ref={containerRef}
    >
      <CardFormTemplate />
      {externalScriptState === ExternalScriptState.ERROR && (
        <ErrorContainer>
          <FormattedMessage id="paymentMethod.description.method_disabled" />
        </ErrorContainer>
      )}
      {hasSavedInstruments && (
        <ChevronButton
          className="card-component__wl-show-saved-card"
          textId="savedInstrument.show.card"
          handleClick={handleShowSavedInstrumentClick}
          icon={ChevronLeft}
        />
      )}
    </Container>
  );
}

export default withCardFormMetrics(
  withLocaleContext(withSaferPayScriptContext(withCardContext(withRouter(CardFormWL))))
);
